import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { flowRegistry } from './internal/flowRegistry';
import { resolveGuidanceStep } from './internal/utils/resolveGuidanceStep';
import type { OnboardingGuidanceStep, OnboardingStepParams } from './types';

type BaseStepParams = {
  onboardingFlow: string;
  stepIndex: number;
  stepDef: OnboardingGuidanceStep;
};

/**
 * Hook to read onboarding-specific query parameters from the URL.
 * Returns the onboarding flow name, step index, and resolved selector if valid,
 * otherwise returns undefined.
 *
 * The onboardingFlow should match an exported flow variable name (e.g., "individualTargetRule").
 * The step should be a numeric index (e.g., "0", "1", "2").
 *
 * Expected query params:
 * - onboardingFlow: The exported name of the flow (e.g., "individualTargetRule")
 * - step: A numeric step index (e.g., "0", "1", "2")
 *
 * Steps that declare `variants` resolve their selector against the DOM (see `resolveGuidanceStep`).
 * Resolution happens synchronously on every render, so a step change is reflected in the same
 * commit. A MutationObserver additionally forces a re-render when a variant's anchor element enters
 * or leaves the DOM after the step first rendered, so a step reached before its variant anchor has
 * mounted (e.g. a cold load / refresh already at the final step, where the anchor is behind an async
 * check) still switches to the variant once the anchor appears.
 *
 * @example
 * ```tsx
 * // URL: ?onboardingFlow=individualTargetRule&step=0
 * const params = useOnboardingQueryParams();
 * if (params) {
 *   log(params.selector); // "install-flag-in-code"
 *   log(params.stepIndex); // 0
 * }
 * ```
 */
export function useOnboardingQueryParams(): OnboardingStepParams | undefined {
  const [searchParams] = useSearchParams();

  const onboardingFlow = searchParams.get('onboardingFlow');
  const step = searchParams.get('step');

  // The URL-derived part of the params (flow, step index, and the step definition), independent of
  // any DOM-based variant resolution. Only recompute when the query parameter values change.
  const base = useMemo<BaseStepParams | undefined>(() => {
    if (!onboardingFlow || !step) {
      return undefined;
    }

    // Step must be a numeric index
    const stepIndex = Number(step);
    if (isNaN(stepIndex) || step !== String(stepIndex)) {
      return undefined;
    }

    // Look up the flow by its exported name
    const flow = flowRegistry[onboardingFlow];
    if (!flow || !flow.steps) {
      return undefined;
    }

    const stepDef = flow.steps[stepIndex];
    if (!stepDef) {
      return undefined;
    }

    return { onboardingFlow, stepIndex, stepDef };
  }, [onboardingFlow, step]);

  // Resolve the (possibly variant) selector synchronously on every render, so a step change is
  // reflected in the same commit rather than a frame later (highlighting, validation, and popover
  // positioning all read this, and would otherwise briefly target the previous step's element).
  // `resolveGuidanceStep` only touches the DOM for steps that declare variants; others return at once.
  const selector = base ? resolveGuidanceStep(base.onboardingFlow, base.stepDef).selector : undefined;

  // A variant's anchor can enter or leave the DOM *after* this step first renders — e.g. a cold load
  // already at the final step, where the banner's anchor mounts once an async check resolves. Watch
  // for that and force a re-render (which re-runs the synchronous resolution above), but only when it
  // actually changes the resolved selector, so unrelated DOM churn doesn't re-render every consumer.
  const selectorRef = useRef(selector);
  useEffect(() => {
    selectorRef.current = selector;
  }, [selector]);

  const [, forceResolve] = useState(0);
  useEffect(() => {
    if (!base?.stepDef.variants?.length) {
      return;
    }
    const observer = new MutationObserver(() => {
      const next = resolveGuidanceStep(base.onboardingFlow, base.stepDef).selector;
      if (next !== selectorRef.current) {
        forceResolve((n) => n + 1);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-guidance-step'],
    });
    return () => observer.disconnect();
  }, [base]);

  return useMemo(
    () => (base ? { onboardingFlow: base.onboardingFlow, stepIndex: base.stepIndex, selector } : undefined),
    [base, selector],
  );
}
